/**
 * Session persistence.
 *
 * The auth store never touches Dexie directly — the same rule every other
 * feature follows. It matters more here than elsewhere: in Phase 5 the offline
 * grant (a cached credential with an explicit expiry, see
 * docs/ARCHITECTURE.md §7.9) is added behind exactly these functions, and no
 * screen or store should notice.
 */

import { db } from "@/db/database";
import type { StoredSession } from "@/db/schema";

/**
 * The session shape, re-exported as this repository's public contract.
 *
 * Consumers import `Session` from here rather than from `@/db/schema`, so the
 * local database stays an implementation detail of the repository layer — the
 * boundary an ESLint rule enforces, and the one that lets Phase 5 change how a
 * session is stored without touching a store or a screen.
 */
export type Session = StoredSession;

export interface SessionPatch {
  accessToken?: string;
  refreshToken?: string;
  activeMerchantId?: string | null;
  activeBranchId?: string | null;
  activeSectionId?: string | null;
}

export const authRepository = {
  /** The persisted session, or null if nobody is signed in on this terminal. */
  async load(): Promise<StoredSession | null> {
    try {
      return (await db.auth.get("current")) ?? null;
    } catch {
      // IndexedDB can be unavailable — private browsing, a locked profile, a
      // quota failure. That must degrade to "signed out", not to a blank screen.
      return null;
    }
  },

  /**
   * Forget every shop's cached data, keeping the signed-in session.
   *
   * For SmartSpaza support leaving a shop they viewed. See
   * `SmartSpazaDatabase.clearShopData` for why a till never calls it.
   */
  async forgetShopData(): Promise<void> {
    await db.clearShopData();
  },

  /**
   * The signed-in user's id, stamped on queued work as `cashier_id`.
   *
   * The server credits an upload to the person who rang it up rather than to
   * whoever is signed in when the queue drains. Without it, cashier A's
   * offline morning uploaded under cashier B's name.
   */
  async cashierId(): Promise<string | null> {
    return (await this.load())?.user.id ?? null;
  },

  async save(session: StoredSession): Promise<void> {
    await db.auth.put({ ...session, key: "current" });
  },

  /** Merge a partial change — a rotated token, a newly selected section. */
  async patch(patch: SessionPatch): Promise<StoredSession | null> {
    const current = await this.load();
    if (!current) return null;

    const next: StoredSession = { ...current, ...patch, key: "current" };
    await db.auth.put(next);
    return next;
  },

  /**
   * Sign out.
   *
   * Clears the session **only**. The outbox, pending sales and stock movements
   * are deliberately untouched: a cashier ending their shift must not take the
   * shop's unsynced takings with them. Those belong to the terminal, not to the
   * person who happened to be signed in.
   */
  async clear(): Promise<void> {
    try {
      await db.auth.clear();
    } catch {
      // Nothing useful to do; the store has already forgotten the session.
    }
  },
};
